import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { useRead } from '/read.js';
import { Icon } from '/icons.js';
import { Discover, Requests } from '/discover.js';

/**
 * The Venues tab.
 *
 * A tab rather than three levels down behind a conditional Settings row, which
 * is where venue setup used to live and why nobody could find it. Sections
 * appear only when they have something in them, so an ordinary member with no
 * venues sees an honest empty screen rather than controls that would refuse.
 *
 * A row opens THAT venue's page. It used to open the generic admin panel, so
 * running a pub meant visiting the screen where the site is managed and finding
 * the pub again in a list. Creating a venue is still an admin act and still
 * lives there; running one does not.
 */
/* Below this many pubs the list is the answer and a search box is furniture.
   The same line the admin roster draws, for the same reason — see admin.js. */
const SEARCH_FROM = 8;

export function VenuesTab({ onGo }) {
  const [round, setRound] = useState(0);
  const [search, setSearch] = useState('');
  // Read through the shared cache, so the copy warmed while another tab was
  // on screen is the one that paints here.
  const [venues, failed] = useRead('/api/venues');
  const error = failed ? (failed.detail || failed.message) : '';

  // GitHub issue #47. Client-side over the payload already in hand, matching
  // the NAME and the detail line both — the line carries the postcode, and a
  // postcode is how you tell two White Harts apart, so searching one without
  // the other would answer the wrong half of the question this list has.
  const query = search.trim().toLowerCase();
  const shown = !query || venues === undefined ? venues : venues.filter(
    (v) => `${v.name} ${venueLine(v)}`.toLowerCase().includes(query));

  return html`
    <div class="stack" data-testid="venues-screen">
      ${error && html`<p class="sub" style="color:var(--danger)">${error}</p>`}

      <label>Venues</label>

      ${venues !== undefined && venues.length >= SEARCH_FROM && html`
        <input data-testid="venues-search" value=${search}
               placeholder="Search venues"
               onInput=${(e) => setSearch(e.target.value)} />`}

      ${venues === undefined
        ? html`<p class="sub">Loading…</p>`
        : venues.length === 0
          ? html`<p class="sub" data-testid="venues-empty">
                   No venues yet. An admin adds them.
                 </p>`
          : shown.length === 0
            ? html`<p class="empty" data-testid="venues-none">
                     No venue matches that.
                   </p>`
          : shown.map((v) => { const line = venueLine(v); return html`
              <button key=${v.venue_id} class="secondary card rowbetween"
                      data-testid=${`venues-item-${v.venue_id}`}
                      onClick=${() => onGo('venue', v.venue_id)}>
                <span style="display:flex;align-items:center;gap:.6rem;
                             min-width:0">
                  <${Icon} name="map-pin" size=${16} />
                  <span style="text-align:left;min-width:0">
                    <span style="display:block">${v.name}</span>
                    ${line && html`
                      <span class="sub" style="text-transform:none"
                            data-testid=${`venues-detail-${v.venue_id}`}>
                        ${line}
                      </span>`}
                  </span>
                </span>
                ${/* Issue #27: the postcode right-justified on the name's own
                      line, with whatever status this row carries beneath it
                      -- so the right column has the same two lines the left
                      one does and they sit level.

                      `white-space:nowrap` because a postcode broken across
                      lines is unreadable, and it is short enough that a long
                      pub name should be the thing that gives way, which is
                      what the min-width:0 above allows. */''}
                <span style="text-align:right;white-space:nowrap">
                  ${v.postcode && html`
                    <span class="sub" style="display:block;
                                             text-transform:none"
                          data-testid=${`venues-postcode-${v.venue_id}`}>
                      ${v.postcode}
                    </span>`}
                  ${(v.in_use || v.my_bundle) && html`
                    <span class="sub" style="display:block">
                      ${v.in_use ? 'Table in use' : v.my_bundle}
                    </span>`}
                </span>
              </button>`; })}

      <${Requests} kind="venue" onChanged=${() => setRound((r) => r + 1)} />
      <${Discover} kind="venue" />
    </div>`;
}

/**
 * The muted second line of a venue row: how many tables, and what a game
 * costs.
 *
 * NOT the postcode, which sits on the NAME line instead (issue #27). While it
 * was here every row was two lines deep, and for most of the league's pubs --
 * which have no table count or price recorded -- that second line held the
 * postcode and nothing else. It belongs beside the name: the postcode is the
 * one field that tells two pubs of the same name apart, which makes it part
 * of the label rather than a detail under it.
 *
 * Each part drops out when its field is empty, so this returns "" rather than
 * a line of separators. A venue with no tables says nothing about tables at
 * all -- "0 tables" is a claim, and an unfilled form is not evidence for it.
 */
export function venueLine(v) {
  const bits = [];
  if (v.tables) bits.push(`${v.tables} ${v.tables === 1 ? 'table' : 'tables'}`);
  if (v.price) bits.push(v.price);
  return bits.join(' · ');
}

/**
 * The names that more than one venue has.
 *
 * Two pubs really can share a name — the league has a White Hart on London
 * Road and another on Worting Road, three miles and one postcode district
 * apart — so `create_venue` does not enforce uniqueness and must not start.
 * What that costs is a picker offering two identical rows, which is a coin
 * toss; the postcode is what tells them apart, and `Venue.postcode` is held
 * separately from the address for exactly this ("an address is 'off the ring
 * road'; a postcode is a destination").
 *
 * Lower-cased, because "The Oak" and "The oak" are the same clash to anybody
 * reading the screen. A Set, so a row can ask about itself without the caller
 * rescanning the list per row.
 */
export function sharedNames(venues) {
  const counts = new Map();
  for (const v of venues || []) {
    const key = (v.name || '').trim().toLowerCase();
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  }
  return new Set([...counts].filter(([, n]) => n > 1).map(([k]) => k));
}

/** Is this venue one of a pair sharing a name? */
export function needsPostcode(v, shared) {
  return shared.has((v?.name || '').trim().toLowerCase());
}
